import type { Edge, Node } from '@xyflow/react'
import { v4 } from 'uuid'
import { NO_REPLY_HANDLE } from '../constants'
import { type AiAgentV2NodeData, FlowNodeTypes, type GenericNode, type RFInstance } from '../types'
import { getDefaultNodeData } from './index'

export const getConnectedNoReplyNode = (rf: RFInstance, nodeId: string) => {
  const edge = rf
    .getNodeConnections({ nodeId, type: 'source' })
    .find((connection) => connection.sourceHandle === NO_REPLY_HANDLE)
  return edge ? { node: rf.getNode(edge.target), edgeId: edge.edgeId } : null
}

// The auto-created reply-timeout target carries its owner's id in
// meta.noReplyTargetOf. React Flow queues addNodes/setNodes/addEdges/setEdges
// and applies them only after the render that called them, so two syncs in one
// render window (the sidebar auto-wires on open, during render) both read
// "nothing wired" from the store and used to create the branch twice — leaving
// duplicate No Reply edges and target nodes. The marker lets the queued
// updaters dedupe against creations still sitting in the queue, and separates
// machine-created targets (safe to rebuild/remove) from nodes the user wired
// in themselves (never touched). Targets created before the marker existed
// shipped deletable: false, so that flag is the legacy signal.
const ownsNoReplyTarget = (node: GenericNode, aiNodeId: string) =>
  node.data?.meta?.noReplyTargetOf === aiNodeId

const isAutoCreatedTarget = (node: Node | GenericNode, aiNodeId: string) =>
  ownsNoReplyTarget(node as GenericNode, aiNodeId) || node.deletable === false

const isNoReplyEdge = (edge: Edge, aiNodeId: string) =>
  edge.source === aiNodeId && edge.sourceHandle === NO_REPLY_HANDLE

const STALE_SMS_DIRECTION = {
  from: '{{Start.message.from}}',
  to: '{{Start.message.to}}',
}

const CANONICAL_SMS_DIRECTION = {
  from: '{{Start.message.to}}',
  to: '{{Start.message.from}}',
}

const repairStaleSmsDirection = (
  rf: RFInstance,
  node: Node,
  aiNodeId: string,
  targetConfigDefaults?: Record<string, unknown>,
): void => {
  if (
    node.type !== FlowNodeTypes.agent_preset_conversation ||
    targetConfigDefaults?.from !== CANONICAL_SMS_DIRECTION.from ||
    targetConfigDefaults?.to !== CANONICAL_SMS_DIRECTION.to
  ) {
    return
  }

  const genericNode = node as GenericNode
  const owner = genericNode.data?.meta?.noReplyTargetOf
  const isOwned = owner === aiNodeId || (!owner && node.deletable === false)
  const config = genericNode.data.config
  const direction = config as Record<string, unknown>
  if (
    !isOwned ||
    direction.from !== STALE_SMS_DIRECTION.from ||
    direction.to !== STALE_SMS_DIRECTION.to
  ) {
    return
  }

  rf.updateNode(node.id, {
    data: {
      ...genericNode.data,
      config: {
        ...config,
        ...CANONICAL_SMS_DIRECTION,
      },
    },
  })
}

/** Queue the creation of a fresh auto target + its no-reply edge, optionally
 * replacing one known stale edge (a dangling or disconnected branch).
 * Existence checks run inside the queued updaters so a re-run before React
 * Flow flushes cannot wire the branch twice; a parked auto target is reused.
 *
 * The canvas is CONTROLLED (useNodesState/useEdgesState), so React Flow's
 * store lags these queued updates by a render — rf.getNodes() inside the edge
 * updater cannot see a node created in the same pass (the edge silently never
 * appeared on the first sidebar open). The node updater therefore hands the
 * target id to the edge updater through a closure; the node queue always
 * flushes first. */
const createNoReplyBranch = (
  rf: RFInstance,
  aiNode: Node<AiAgentV2NodeData>,
  desiredType: FlowNodeTypes,
  staleEdgeId?: string,
  targetConfigDefaults?: Record<string, unknown>,
): void => {
  let targetId: string | null = null
  rf.setNodes((nodes) => {
    const parked = nodes.find((node) => ownsNoReplyTarget(node, aiNode.id))
    if (parked) {
      targetId = parked.id
      return nodes
    }
    targetId = v4()
    const data = getDefaultNodeData(desiredType, () => nodes)
    data.meta = { ...data.meta, noReplyTargetOf: aiNode.id }
    if (targetConfigDefaults) {
      data.config = { ...data.config, ...targetConfigDefaults }
    }
    return [
      ...nodes,
      {
        id: targetId,
        type: desiredType,
        position: { x: aiNode.position.x - 500, y: aiNode.position.y + 300 },
        data,
      } as GenericNode,
    ]
  })
  rf.setEdges((edges) => {
    const kept = staleEdgeId ? edges.filter((edge) => edge.id !== staleEdgeId) : edges
    if (kept.some((edge) => isNoReplyEdge(edge, aiNode.id))) return kept
    if (!targetId) return kept
    return [
      ...kept,
      {
        id: v4(),
        source: aiNode.id,
        target: targetId,
        sourceHandle: NO_REPLY_HANDLE,
      },
    ]
  })
}

/** Remove the given no-reply edges plus any of their auto-created targets
 * that no other edge still uses. User-wired targets are never removed. */
const removeNoReplyEdges = (rf: RFInstance, aiNodeId: string, edges: Edge[]): void => {
  if (!edges.length) return
  const edgeIds = new Set(edges.map((edge) => edge.id))
  const remaining = rf.getEdges().filter((edge) => !edgeIds.has(edge.id))
  const orphanIds = new Set(
    edges
      .map((edge) => edge.target)
      .filter((id) => {
        const node = rf.getNode(id)
        return (
          !!node &&
          isAutoCreatedTarget(node, aiNodeId) &&
          !remaining.some((edge) => edge.source === id || edge.target === id)
        )
      }),
  )
  rf.setEdges((eds) => eds.filter((edge) => !edgeIds.has(edge.id)))
  if (orphanIds.size) {
    rf.setNodes((nodes) => nodes.filter((node) => !orphanIds.has(node.id)))
  }
}

/**
 * Single owner of the chat AI node's reply-timeout branch — the sibling of
 * syncVoicemailTarget, anchored on the node's "no reply" exit port instead of
 * a synthetic intent (chat intents become LLM handoff tools, and the backend
 * already fires the "no reply" exit itself when the reply timeout lapses).
 *
 * The branch is fully editable: node and edge are created deletable, and the
 * canvas-level isEditing gates (deleteKeyCode / onBeforeDelete) keep deletion
 * edit-mode-only. Deleting the edge clears no_reply_next_node via
 * onEdgesDelete in use-flow-props.
 *
 * - desiredType null (wait turned off): remove every no-reply edge AND any
 *   auto-created target nothing else uses — no orphan End Conversation left
 *   squatting on the canvas. Targets the user wired in themselves stay.
 * - nothing wired yet: create the branch (see createNoReplyBranch).
 * - connected target type differs: rebuild it with fresh defaults ONLY if it
 *   is machine-created; a user-wired target is disconnected and replaced by a
 *   fresh auto target instead — never mutated, its config could be authored.
 *   Same type is left untouched — rf.updateNode replaces node.data wholesale
 *   and would wipe the target's saved config.
 * - more than one no-reply edge wired (flows polluted by the pre-dedupe
 *   builder): keep the connected one, drop the surplus edges and any of their
 *   auto-created targets nothing else uses.
 */
export const syncNoReplyTarget = (
  rf: RFInstance,
  aiNode: Node<AiAgentV2NodeData>,
  desiredType: FlowNodeTypes | null,
  targetConfigDefaults?: Record<string, unknown>,
): void => {
  const connected = getConnectedNoReplyNode(rf, aiNode.id)

  if (!desiredType) {
    if (!connected) return
    removeNoReplyEdges(
      rf,
      aiNode.id,
      rf.getEdges().filter((edge) => isNoReplyEdge(edge, aiNode.id)),
    )
    return
  }

  if (!connected?.node) {
    // connected without a node = a dangling edge whose target was removed;
    // hand its id over so the fresh branch replaces it.
    createNoReplyBranch(rf, aiNode, desiredType, connected?.edgeId, targetConfigDefaults)
    return
  }

  const surplus = rf
    .getEdges()
    .filter((edge) => isNoReplyEdge(edge, aiNode.id) && edge.id !== connected.edgeId)
  removeNoReplyEdges(rf, aiNode.id, surplus)

  if (connected.node.type === desiredType) {
    repairStaleSmsDirection(rf, connected.node, aiNode.id, targetConfigDefaults)
    return
  }

  if (isAutoCreatedTarget(connected.node, aiNode.id)) {
    const data = getDefaultNodeData(desiredType, () => rf.getNodes())
    data.meta = { ...data.meta, noReplyTargetOf: aiNode.id }
    if (targetConfigDefaults) {
      data.config = { ...data.config, ...targetConfigDefaults }
    }
    rf.updateNode(connected.node.id, { type: desiredType, data })
    return
  }

  // User-wired target of a different type: leave the node alone, swap the
  // branch over to a fresh auto target of the requested type.
  createNoReplyBranch(rf, aiNode, desiredType, connected.edgeId, targetConfigDefaults)
}
